import axios, { AxiosProgressEvent } from "axios";
import * as client from "../client";
import { TemplateUseCase } from "../pages/specialty-assistant/template/data";
import { FinancialModelAuditSource } from "../pages/specialty-assistant/layers/financialModelAuditTypes";
import { ROUTES } from "../routes";
import { buildChannel } from "../util";
import * as Token from "./auth_token";
import { errorDetailFromBinaryResponse } from "./errors";
import {
  MAX_UPLOAD_BATCH_BYTES,
  batchFilesBySize,
  rejectOversizedFiles,
} from "./uploadBatches";
export * as apiTypes from "../client/types.gen";
import * as config from "../config";

// Cat types whose failed run can be recovered through /cat/{report_id}/resume.
// What that means is the server's call and differs by vendor: the chunk/peril
// pipelines pick their failed stages up in place and hand back the same batch,
// while SpatialKat reprices the stored losses into a new one. Either way, poll
// the batch_id the response returns. Derived from the generated client so the
// union tracks the backend's CatTypeParam literal automatically.
export type EltPipelineType = client.ResumeCatPipelineData["type"];

// Cat types whose completed runs can be repriced from their stored ground-up
// losses, no vendor round-trip. Derived from its own endpoint, but the backend
// types all three of these endpoints with the same CatTypeParam, so this and
// EltPipelineType cannot diverge without a change there.
export type RerunFinancialsCatType = client.RerunCatFinancialData["type"];

// Every cat vendor with a run history, pipeline or not. Derived from the
// generated client so the union tracks the backend's CatTypeParam literal.
export type CatVendorType = client.ActivateCatBatchData["type"];

// Every run /cat/{report_id}/run starts and /cat/status polls: the cat vendors
// and the raters.
export type RunType = client.CatRunRef["type"];

const baseUrl = () => {
  const channel = buildChannel();
  switch (channel) {
    case "prod":
      return config.BASE_URL_PROD;
    case "dev":
      return config.BASE_URL_DEV;
    case "dev-vm":
      return config.BASE_URL_DEV_VM;
  }
};

// Structured metadata a binary endpoint returns beside the bytes, as JSON in
// this header. Mirrors LUMINOR_RESPONSE_HEADER in the backend's
// src/shared/response_meta.py, which owns the per-endpoint shapes; the header
// is outside the OpenAPI schema, so the client-side types below are kept by
// hand.
const RESPONSE_META_HEADER = "X-Luminor-Response";

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const isStringArray = (value: unknown): value is string[] =>
  Array.isArray(value) && value.every((v) => typeof v === "string");

const fetchBinary = async (options: {
  method: "get" | "post";
  path: string;
  body?: unknown;
  contentType?: string;
}): Promise<{ data: Blob; filename?: string; meta: unknown }> => {
  const url = `${baseUrl()}${options.path}`;
  const config = {
    responseType: "arraybuffer" as const,
    headers: { Authorization: `Bearer ${Token.getToken()}` },
  };
  const response =
    options.method === "get"
      ? await axios.get(url, config)
      : await axios.post(url, options.body, config);
  const headers = response.headers;
  const getHeader = (name: string): string | undefined => {
    if (typeof headers.get === "function") {
      const value = headers.get(name);
      if (typeof value === "string") return value;
    }
    return undefined;
  };
  const disposition = getHeader("Content-Disposition");
  const blob = new Blob([response.data as ArrayBuffer], {
    type:
      options.contentType ??
      getHeader("Content-Type") ??
      "application/octet-stream",
  });
  const rawMeta = getHeader(RESPONSE_META_HEADER);
  let meta: unknown = undefined;
  if (rawMeta) {
    try {
      meta = JSON.parse(rawMeta);
    } catch {
      // A malformed header should never block the file download.
    }
  }
  return {
    data: blob,
    filename: disposition
      ? /filename="([^"]*)"/.exec(disposition)?.[1]
      : undefined,
    meta,
  };
};

client.OpenAPI.BASE = baseUrl();
client.OpenAPI.interceptors.request.use((request) => {
  const token = Token.getToken();
  if (token) {
    if (request.headers === undefined) {
      request.headers = {};
    }
    request.headers["Authorization"] = `Bearer ${token}`;
  }

  return request;
});

client.OpenAPI.interceptors.response.use((response) => {
  if (response.status === 401 && !response.config.url?.endsWith("login")) {
    Token.logOut();
    window.location.href = ROUTES.LANDING;
  }
  return response;
});

let URL_PROGRESS_CALLBACKS: [string, (e: AxiosProgressEvent) => void][] = [];

client.OpenAPI.interceptors.request.use((request) => {
  request.onUploadProgress = (e: AxiosProgressEvent) => {
    for (const [url, cb] of URL_PROGRESS_CALLBACKS) {
      if (request.url?.startsWith(url)) {
        cb(e);
      }
    }
  };
  return request;
});

export const login = async (
  username: string,
  password: string
): Promise<
  | { success: true; clientId: string; user: client.User }
  | { success: false; failureReason: string }
> => {
  return await client
    .login({ formData: { username, password } })
    .then((r) => {
      Token.setToken(r.access_token);
      return {
        success: true as const,
        clientId: r.client_id,
        user: r.user,
      };
    })
    .catch((rawE: client.ApiError) => {
      if (rawE.status === 422) {
        const e = rawE as client.HttpValidationError;
        console.error("login failed: ", e.detail);
      }
      return { success: false, failureReason: "unknown" };
    });
};

export const logAmplitudeEventRemotely = async (
  eventName: string,
  eventProperties: {
    [key: string]: string | number;
  }
) => {
  return client.logEvent({
    requestBody: { event_name: eventName, event_properties: eventProperties },
  });
};

export type LOB =
  client.AsgardListReportsPostData["requestBody"]["lobs"][number];
export const listReports = async (
  lobs: LOB[]
): Promise<client.ReportMetadataResponse[]> => {
  return client
    .asgardListReportsPost({ requestBody: { lobs } })
    .then((response) => response.reports);
};

// Pass a reportId to check one report; pass null to aggregate across all the
// user's reports (dashboard).
export const fetchFreshness = async (params: {
  reportId: string | null;
  clientModifiedAtTs: string | null;
  clientCommentsModifiedAtTs: string | null;
}): Promise<client.FreshnessResponse> => {
  return client.asgardFreshness({
    requestBody: {
      report_id: params.reportId,
      client_modified_at_ts: params.clientModifiedAtTs,
      client_comments_modified_at_ts: params.clientCommentsModifiedAtTs,
    },
  });
};

export { getSubmissionAnalytics } from "../client";
export {
  getExposureMapSummary,
  getExposureMapLocations,
  getAreaExposure,
  getPolicyFootprint,
  getActiveStorms,
  getPastStorms,
} from "../client";

export const listReport = async (
  reportId: string,
  includeAddresses?: boolean
) => {
  return client.asgardListReport({
    reportId,
    includeAddresses,
  });
};

export const loadAddresses = async (
  reportId: string,
  sovId: string
): Promise<{ addresses: client.Address[] }> => {
  return await client.asgardLoadAddresses({
    reportId,
    sovId,
  });
};

export const fetchEmailThreads = async (reportId: string) => {
  return client.asgardEmailThreads({ reportId });
};

export const uploadEmailThread = async (
  reportId: string,
  files: File[]
): Promise<client.UploadEmailThreadResponse> => {
  return client.asgardUploadEmailThread({
    reportId,
    formData: { files },
  });
};

export const classifyStagedAttachment = async (
  reportId: string,
  stagingId: string,
  attachmentName: string
): Promise<client.ClassifyStagedAttachmentResponse> => {
  return client.asgardClassifyStagedAttachment({
    requestBody: {
      report_id: reportId,
      staging_id: stagingId,
      attachment_name: attachmentName,
    },
  });
};

export const extractStagedAttachments = async (
  reportId: string,
  selections: client.StagedAttachmentSelection[]
): Promise<client.ExtractStagedAttachmentsResponse> => {
  return client.asgardExtractStagedAttachments({
    requestBody: { report_id: reportId, selections },
  });
};

export const deleteEmailThread = async (
  reportId: string,
  conversationId: string
): Promise<string[]> => {
  return (
    await client.asgardDeleteEmailThread({
      reportId,
      requestBody: { conversation_id: conversationId },
    })
  ).deleted_files;
};

export type UpdateReport = {
  assignee?: client.UpdateAsignee;
  status?: client.UpdateStatus;
  additionalAssignees?: client.AdditionalAssigneeInfo;
  sovs?: client.Sov[];
  lossRunInfo?: client.LossRunInfo;
  reviewQuestionResponse?: client.ReviewQuestionResponse;
  companyInfo?: client.CompanyInfo;
  attachmentInfo?: client.AttachmentInfo;
  referralText?: client.ReferralText;
  underwritingRationale?: string;
  archiveAt?: boolean;
  rerunReport?: boolean;
  rerunComponents?: client.RerunComponent[];
  // Narrows rerunComponents to these attachments (the email body is named by
  // its EMAIL_BODY_SOURCE_NAME); omitted means every attachment of each
  // selected component.
  rerunAttachments?: string[];
  brokerContact?: client.UpdateBrokerContact;
  limitAndSublimitInfo?: client.LimitAndSublimitInfo;
  policyInfo?: client.PolicyInfo;
  sublimitInfo?: client.SublimitInfo;
  deductibleInfo?: client.AdvancedDeductibleInfo;
  advancedLimitInfo?: client.AdvancedLimitInfo;
  layerInfo?: client.Layer[];
  googleGroupEmail?: client.UpdateGoogleGroup;
  submissionRecipients?: string[];
  // Sort-only reorders. These touch only display-order keys on existing rows,
  // so they never trigger a rerun, function regen, or gate-contribution refresh.
  limitOrder?: client.ExcerptSortUpdate[];
  deductibleOrder?: client.ExcerptSortUpdate[];
  sublimitOrder?: client.SublimitSortUpdate[];
};
export const updateReport = async (
  reportId: string,
  updates: UpdateReport
): Promise<client.UpdateReportResponse> => {
  return await client.asgardUpdateReport({
    requestBody: {
      report_id: reportId,
      update_assignee: updates.assignee ?? null,
      update_status: updates.status ?? null,
      update_additional_assignees: updates.additionalAssignees ?? null,
      // Addresses are never mutated via this API (They are dropped on the backend)
      // However, these represent a lot of data, so don't send them over the wire.
      update_sovs: updates.sovs
        ? updates.sovs.map((sov) => ({ ...sov, addresses: [] }))
        : null,
      update_loss_runs_info: updates.lossRunInfo ?? null,
      update_review_question_response: updates.reviewQuestionResponse ?? null,
      update_company_info: updates.companyInfo ?? null,
      update_attachment_info: updates.attachmentInfo ?? null,
      rerun_report: updates.rerunReport ?? null,
      rerun_components: updates.rerunComponents ?? null,
      rerun_attachments: updates.rerunAttachments ?? null,
      update_referral_text: updates.referralText ?? null,
      update_underwriting_rationale: updates.underwritingRationale ?? null,
      update_broker_contact: updates.brokerContact ?? null,
      update_limit_info: updates.limitAndSublimitInfo ?? null,
      update_policy_info: updates.policyInfo ?? null,
      update_sublimit_info: updates.sublimitInfo ?? null,
      update_deductible_info: updates.deductibleInfo ?? null,
      update_advanced_limit_info: updates.advancedLimitInfo ?? null,
      update_layer_info: updates.layerInfo ?? null,
      update_google_group_email: updates.googleGroupEmail ?? null,
      update_submission_recipients: updates.submissionRecipients ?? null,
      update_limit_order: updates.limitOrder ?? null,
      update_deductible_order: updates.deductibleOrder ?? null,
      update_sublimit_order: updates.sublimitOrder ?? null,
    },
  });
};

export type UpdateAddresses = {
  addresses: client.Address[];
};
export const updateAddresses = async (
  report_id: string,
  update: UpdateAddresses
): Promise<client.UpdateAddressResponse> => {
  return await client.asgardUpdateAddress({
    requestBody: { report_id, addresses: update.addresses },
  });
};

export const addManualAddress = async (
  report_id: string,
  address: client.RawAddress
): Promise<client.AddManualAddressResponse> => {
  return await client.asgardAddManualAddress({
    requestBody: { report_id, address: address },
  });
};

export const downloadLossRun = async (
  parsedLossRuns: client.UserGeneratedLossRunByYear[]
) => {
  const { data } = await fetchBinary({
    method: "post",
    path: "/asgard/download_loss_run",
    body: { loss_run_years: parsedLossRuns },
  });
  return data;
};

export const downloadPdfZip = async (folderName: string) => {
  const { data } = await fetchBinary({
    method: "get",
    path: `/adonis/download_pdfs?folder_name=${folderName}`,
  });
  return data;
};

export { listUsers } from "../client";

export { listContactsEndpoint as listCRMContacts } from "../client";

export const createCRMContact = async (
  requestBody: client.CreateContactRequest
): Promise<client.CreateContactResponse> => {
  return await client.createContactEndpoint({ requestBody });
};

export const updateCRMContact = async (
  contactId: string,
  requestBody: client.UpdateContactRequest
): Promise<client.UpdateContactResponse> => {
  return await client.updateContactEndpoint({
    contactId,
    requestBody,
  });
};

export const listAdditionalFiles = async (
  reportId: string
): Promise<client.ListAdditionalFilesResponse> => {
  return await client.asgardListAdditionalFiles({ reportId });
};

/**
 * Uploads `files` in as many requests as the server's body size limit needs.
 * Throws `FileTooLargeError`, before uploading anything, if a file cannot fit
 * in one request. A failure partway through leaves the earlier batches
 * uploaded. `onUploadProgress` reports progress across all batches.
 */
export const uploadAdditionalFile = async (
  reportId: string,
  files: File[],
  onUploadProgress: (e: AxiosProgressEvent) => void,
  isScreenshot: boolean
): Promise<void> => {
  rejectOversizedFiles(files, MAX_UPLOAD_BATCH_BYTES);
  const totalBytes = files.reduce((sum, f) => sum + f.size, 0);
  let bytesInEarlierBatches = 0;
  let batchBytes = 0;
  const callback = (e: AxiosProgressEvent) => {
    const batchFraction = e.progress ?? (e.total ? e.loaded / e.total : 0);
    const loaded = bytesInEarlierBatches + batchFraction * batchBytes;
    onUploadProgress({
      ...e,
      loaded,
      total: totalBytes,
      progress: totalBytes > 0 ? loaded / totalBytes : batchFraction,
    });
  };
  URL_PROGRESS_CALLBACKS.push([
    `${client.OpenAPI.BASE}/asgard/upload_additional_files`,
    callback,
  ]);
  try {
    // Sequential, not parallel: with a classification, each request rewrites
    // the report's attachment_info, so concurrent requests could drop each
    // other's entries.
    for (const batch of batchFilesBySize(files, MAX_UPLOAD_BATCH_BYTES)) {
      batchBytes = batch.reduce((sum, f) => sum + f.size, 0);
      await client.asgardUploadAdditionalFile({
        reportId: reportId,
        classificationForAllFiles: isScreenshot ? "screenshot" : null,
        sourceForAllFiles: isScreenshot ? "screenshot_tool" : "user_upload",
        formData: {
          files: batch,
        },
      });
      bytesInEarlierBatches += batchBytes;
    }
  } finally {
    URL_PROGRESS_CALLBACKS = URL_PROGRESS_CALLBACKS.filter(
      ([, cb]) => cb != callback
    );
  }
};

export const deleteAttachment = async (
  reportId: string,
  filename: string
): Promise<client.AttachmentInfo> => {
  return await client.asgardDeleteAttachment({
    requestBody: { report_id: reportId, filename },
  });
};

export const deleteAdditionalFile = async (
  reportId: string,
  files: string[]
): Promise<void> => {
  await client.asgardDeleteAdditionalFiles({
    reportId,
    requestBody: { files },
  });
};

export const testDeductibleOrLimitFunction = async (
  reportId: string,
  filename: string | null,
  perilType: client.peril_type,
  functionType: client.function_type
) => {
  return await client.asgardTestPythonFunction({
    reportId,
    requestBody: {
      peril_type: perilType,
      extracted_filename: filename,
      function_type: functionType,
    },
  });
};

export const downloadAdditionalFile = async (
  reportId: string,
  file: string
) => {
  const { data } = await fetchBinary({
    method: "post",
    path: "/asgard/download_additional_file",
    body: { report_id: reportId, file },
  });
  return data;
};

export const downloadThreadAttachment = async (
  reportId: string,
  threadUrl: string,
  messageId: string,
  attachmentId: string,
  filename: string
) => {
  const { data } = await fetchBinary({
    method: "post",
    path: "/asgard/download_thread_attachment",
    body: {
      report_id: reportId,
      thread_url: threadUrl,
      message_id: messageId,
      attachment_id: attachmentId,
      filename,
    },
  });
  return data;
};

export const generateBulkEdit = async (
  reportId: string,
  field: string,
  instructions: string
): Promise<[client.Address[], client.Address[]]> => {
  const response = await client.generateBulkEdit({
    requestBody: { report_id: reportId, field, instructions },
  });
  return [response.before, response.after];
};

export const downloadReportFile = async (
  reportId: string,
  filename: string
) => {
  const { data } = await fetchBinary({
    method: "post",
    path: "/asgard/download_report_file",
    body: { report_id: reportId, filename: filename },
  });
  return data;
};

export const loadAddressesForMap = async (lobs: LOB[]) => {
  return await client.asgardLoadAddressesForMap({
    requestBody: { lobs },
  });
};

// target_type/target_key scope a comment: null is report-level (the default),
// "loss_year" with the policy year in target_key scopes it to a loss-run year.
export const getBatchComments = async (
  reportIds: string[],
  targetType: client.CommentTargetType | null = null
) => {
  return client.asgardBatchComments({
    requestBody: { report_ids: reportIds, target_type: targetType },
  });
};

export const addComment = async (
  reportId: string,
  message: string,
  targetType: client.CommentTargetType | null = null,
  targetKey: string | null = null
) => {
  return await client.asgardAddComment({
    requestBody: {
      report_id: reportId,
      message,
      target_type: targetType,
      target_key: targetKey,
    },
  });
};

export const deleteComment = async (
  reportId: string,
  commentId: string,
  targetType: client.CommentTargetType | null = null
) => {
  return await client.asgardDeleteComment({
    requestBody: {
      report_id: reportId,
      comment_id: commentId,
      target_type: targetType,
    },
  });
};

export const editComment = async (
  reportId: string,
  commentId: string,
  message: string,
  targetType: client.CommentTargetType | null = null
) => {
  return await client.asgardEditComment({
    requestBody: {
      report_id: reportId,
      comment_id: commentId,
      message,
      target_type: targetType,
    },
  });
};

export const generateUnderwritingPdf = async (
  report_id: string,
  html_content: string
) => {
  const { data } = await fetchBinary({
    method: "post",
    path: "/asgard/generate_underwriting_pdf",
    body: { report_id, html_content },
    contentType: "application/pdf",
  });
  return data;
};

export const quoteIndicationPreflight = async (
  reportId: string,
  layerId: string,
  perilsInsured: string
) => {
  return client.asgardQuoteIndicationPreflight({
    requestBody: {
      report_id: reportId,
      layer_id: layerId,
      perils_insured: perilsInsured,
    },
  });
};

export const generateQuoteIndicationDraft = async (
  reportId: string,
  layerId: string,
  perilsInsured: string,
  premiumRange: string
) => {
  return client.asgardGenerateQuoteIndicationDraft({
    requestBody: {
      report_id: reportId,
      layer_id: layerId,
      perils_insured: perilsInsured,
      premium_range: premiumRange,
    },
  });
};

export const quoteIndicationHtml = async (
  reportId: string,
  layerId: string,
  perilsInsured: string,
  premiumRange: string
) => {
  return client.asgardQuoteIndicationHtml({
    requestBody: {
      report_id: reportId,
      layer_id: layerId,
      perils_insured: perilsInsured,
      premium_range: premiumRange,
    },
  });
};

export const generateReferralDraft = async (
  reportId: string,
  htmlContent: string
) => {
  return client.asgardGenerateReferralDraft({
    requestBody: {
      report_id: reportId,
      html_content: htmlContent,
    },
  });
};

export const downloadUawSheet = async (reportId: string) => {
  const { data } = await fetchBinary({
    method: "post",
    path: "/asgard/download_uaw",
    body: { report_id: reportId },
  });
  return data;
};

export const deleteReport = async (reportId: string) => {
  return await client.asgardDeleteReport({
    requestBody: { report_id: reportId },
  });
};

export const fetchEqbdRaterResults = async (
  reportId: string,
  batchId: string
): Promise<client.EqbdRaterResultsResponse> => {
  return client.getEqbdRaterResults({
    reportId,
    batchId,
  });
};

export const precheck = async (
  reportId: string
): Promise<client.PrecheckResponse> => {
  return client.precheck({ reportId });
};

export const ALL_JOB_STATUSES: client.WorkStatus[] = [
  ...client.$WorkStatus.enum,
];

// `includeAllRuns` also returns runs that don't back the report's live results.
export const listReportJobs = async (
  reportIds: string[],
  statuses: client.WorkStatus[],
  includeAllRuns: boolean
) => {
  const response = await client.asgardListReportJobs({
    requestBody: {
      report_ids: reportIds,
      statuses,
      include_all_runs: includeAllRuns,
    },
  });
  return response.jobs;
};

export const listClearanceMetadata = async (
  reportIds: string[]
): Promise<client.ReportClearanceEntry[]> => {
  const response = await client.asgardListClearanceMetadata({
    requestBody: { report_ids: reportIds },
  });
  return response.items;
};

export const downloadRaterWorkbook = async (
  reportId: string,
  batchId: string
): Promise<{ data: Blob; filename?: string }> => {
  return fetchBinary({
    method: "get",
    path: `/rater/download/${reportId}/${batchId}`,
    contentType:
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
};

export const fetchRateComponents = async (
  reportId: string,
  batchId: string
): Promise<client.RateComponentsResponse> => {
  return client.getRateComponents({
    reportId,
    batchId,
  });
};

export const previewEml = async (
  reportId: string
): Promise<{ subject: string; eml: string }> => {
  return await client.asgardEmlPreview({
    requestBody: { report_id: reportId },
  });
};

export const downloadReportHistory = async (
  reportIds: string[],
  downloadType: client.download_type,
  exportFormat: client.export_format = "xlsx"
) => {
  const { data } = await fetchBinary({
    method: "post",
    path: "/asgard/download_report_history",
    body: {
      report_ids: reportIds,
      download_type: downloadType,
      export_format: exportFormat,
    },
  });
  return data;
};

export const uploadBdxForPerilBreakdown = async (
  file: File
): Promise<{ data: Blob; filename?: string; warnings?: string[] }> => {
  const formData = new FormData();
  formData.append("file", file);
  try {
    const { data, filename, meta } = await fetchBinary({
      method: "post",
      path: "/asgard/bdx/peril_breakdown",
      body: formData,
    });
    // BdxPerilBreakdownMeta in the backend's bdx_endpoints.py.
    const warnings =
      isRecord(meta) && isStringArray(meta.warnings)
        ? meta.warnings
        : undefined;
    return { data, filename, warnings };
  } catch (e) {
    throw new Error(
      errorDetailFromBinaryResponse(e) ?? "Failed to process the BDX file."
    );
  }
};

export const getUpdatingReports = async (since: Date) => {
  return await client.asgardListUpdatingReports({
    requestBody: { since: since.toISOString() },
  });
};

export { brokerAnalytics as getBrokerChartData } from "../client";

export { getSubmissionStatusCounts } from "../client";

export const listPolicyFlyApplications = async (
  statuses: string
): Promise<client.PolicyFlyApplicationRow[]> => {
  return client
    .getPolicyflyApplications({ statuses })
    .then((response) => response.applications);
};

export const exportBrokerData = async () => {
  const { data } = await fetchBinary({
    method: "post",
    path: "/crm/broker_export",
    body: {},
  });
  return data;
};

export { asgardListTemplates as listTemplates } from "../client";

export const createTemplate = async (
  template: File,
  useCase: TemplateUseCase
) => {
  return await client.asgardCreateTemplate({
    formData: { template },
    useCase,
  });
};

export const deleteTemplate = async (requestBody: client.DeleteTemplate) => {
  return await client.asgardDeleteTemplate({
    requestBody,
  });
};
export const updateTemplate = async (requestBody: client.UpdateTemplate) => {
  return await client.asgardUpdateTemplate({
    requestBody,
  });
};

export const downloadTemplate = async (template: client.Template) => {
  const { data } = await fetchBinary({
    method: "post",
    path: "/asgard/download_template",
    body: { template_id: template.template_id },
  });
  return data;
};

export const generatePolicyNumbers = async (reportId: string) => {
  return await client.asgardGeneratePolicyNumbers({
    requestBody: { report_id: reportId },
  });
};

export const generatePolicyFlyApplication = async (
  reportId: string,
  perilsByLayer: Record<string, string>,
  additionalLayerId?: string
) => {
  return await client.asgardGeneratePolicyFlyApplication({
    requestBody: {
      report_id: reportId,
      perils_by_layer: perilsByLayer,
      additional_layer_id: additionalLayerId ?? null,
    },
  });
};

export const updatePolicyFlyApplication = async (
  reportId: string,
  suffix: string,
  perilsInsuredDescription: string
) => {
  return await client.asgardUpdatePolicyFlyApplication({
    requestBody: {
      report_id: reportId,
      suffix: suffix,
      perils_insured_description: perilsInsuredDescription,
    },
  });
};

export const linkPolicyFlyApplication = async (
  reportId: string,
  layerId: string,
  suffix: string,
  url: string
) => {
  return await client.asgardLinkPolicyFlyApplication({
    requestBody: {
      report_id: reportId,
      layer_id: layerId,
      suffix: suffix,
      url: url,
    },
  });
};

export const unlinkPolicyFlyApplication = async (
  reportId: string,
  suffix: string
) => {
  return await client.asgardUnlinkPolicyFlyApplication({
    requestBody: {
      report_id: reportId,
      suffix: suffix,
    },
  });
};

// Unified CAT results endpoint
export const catResults = async (
  reportId: string,
  batchId?: string
): Promise<client.CatResultsResponse> => {
  return client.getCatResults({
    reportId,
    batchId: batchId ?? null,
  });
};

// On-demand per-layer PMI at a chosen participation share (fraction of the
// layer). Computed against the current in-force book; nothing persisted.
export const layerPmi = async (
  reportId: string,
  layerId: string,
  share: number
): Promise<client.LayerPmiResponse> => {
  return client.getLayerPmi({ reportId, layerId, share });
};

export const sendClearanceEmail = async (
  reportId: string,
  status: client.status
) => {
  return client.asgardSendClearanceEmail({
    requestBody: {
      report_id: reportId,
      status: status,
    },
  });
};

export const runFixSov = async (
  reportId: string,
  instructions: string | null
): Promise<client.RunFixSovResponse> => {
  return client.asgardRunFixSov({
    requestBody: {
      report_id: reportId,
      instructions,
    },
  });
};

// Stores per-file guidance for the limit/deductible extraction LLM (null
// clears it) and queues a function regen for the report.
export const setFunctionInstructions = async (
  reportId: string,
  filename: string,
  functionType: client.function_type,
  instructions: string | null
): Promise<client.SetFunctionInstructionsResponse> => {
  return client.asgardSetFunctionInstructions({
    requestBody: {
      report_id: reportId,
      filename,
      function_type: functionType,
      instructions,
    },
  });
};

// ── Any CAT vendor ──────────────────────────────────────────────────────────

// Starts each run and returns one outcome per run, in order. Poll catRunStatuses
// with each started run's request_id.
export const runCatModel = async (
  reportId: string,
  runs: client.CatRunRequest["runs"]
): Promise<client.CatRunResult> => {
  return client.runCatModel({ reportId, requestBody: { runs } });
};

// One status per run, in request order.
export const catRunStatuses = async (
  runs: client.CatRunRef[]
): Promise<client.CatStatusResult> => {
  return client.catRunStatuses({ requestBody: { runs } });
};

// ── RMS ELT ─────────────────────────────────────────────────────────────────

export const rmsEltJobHistory = async (
  reportId: string
): Promise<client.CatJobHistoryResponse> => {
  return client.getCatJobHistory({
    reportId,
    type: "rms_elt",
  });
};

export const downloadAllRmsEltArtifacts = async (
  reportId: string,
  batchId: string
): Promise<{ data: Blob; filename?: string }> => {
  return fetchBinary({
    method: "get",
    path: `/cat/download_all/${reportId}/${batchId}?type=rms_elt`,
    contentType: "application/zip",
  });
};

export const enqueueSindriReplacementCost = async (
  reportId: string
): Promise<client.SindriEnqueueResponse> => {
  return client.enqueueReplacementCost({
    requestBody: { report_id: reportId },
  });
};

export const rerunCatFinancials = async (
  reportId: string,
  type: RerunFinancialsCatType,
  batchId: string,
  activate: boolean
): Promise<client.CatRerunFinancialResult> => {
  return client.rerunCatFinancial({
    reportId,
    type,
    batchId,
    activate,
  });
};

// ``mode`` is meaningful for icm_ylt only: "resume" keeps each failed
// chunk's ICM status URL (continue polling); "restart" drops it so
// re-dispatched chunks submit fresh ICM ratings — the escape hatch for a
// run stuck on a status URL that never resolves. The backend rejects
// "restart" for the RMS types.
export const resumeCatPipeline = async (
  reportId: string,
  type: EltPipelineType,
  batchId: string,
  mode: "resume" | "restart"
): Promise<client.CatResumeResult> => {
  return client.resumeCatPipeline({
    reportId,
    type,
    batchId,
    mode,
  });
};

// Promotes an already-completed run to the report's live results for its
// vendor: the run's stored results become the ones every CAT read resolves
// to, and the rest of that vendor's history goes inactive. Nothing is
// re-modeled.
export const activateCatBatch = async (
  reportId: string,
  type: CatVendorType,
  batchId: string
): Promise<client.CatActivateResult> => {
  return client.activateCatBatch({
    reportId,
    type,
    batchId,
  });
};

export const catVendorEnrichment = async (
  reportId: string
): Promise<client.VendorEnrichmentState> => {
  return client.getVendorEnrichment({ reportId });
};

// Switches each selected vendor onto the run it already holds under the chosen
// source; nothing is re-modeled.
export const setCatVendorEnrichment = async (
  reportId: string,
  selections: client.VendorEnrichmentSelection[]
): Promise<client.VendorEnrichmentState> => {
  return client.setVendorEnrichment({ reportId, requestBody: { selections } });
};

// ── ICM YLT ─────────────────────────────────────────────────────────────────

export const icmYltJobHistory = async (
  reportId: string
): Promise<client.CatJobHistoryResponse> => {
  return client.getCatJobHistory({
    reportId,
    type: "icm_ylt",
  });
};

export const downloadAllIcmYltArtifacts = async (
  reportId: string,
  batchId: string
): Promise<{ data: Blob; filename?: string }> => {
  return fetchBinary({
    method: "get",
    path: `/cat/download_all/${reportId}/${batchId}?type=icm_ylt`,
    contentType: "application/zip",
  });
};

// ── Enrichment (bulk, admin) ─────────────────────────────────────────────────

export const bulkEnrichSubmissions = async (
  reportIds: string[],
  sources: client.BulkEnrichmentRequest["sources"]
): Promise<client.BulkEnrichmentResponse> => {
  return client.bulkEnrichSubmissions({
    requestBody: { report_ids: reportIds, sources },
  });
};

// ── SpatialKat (KatRisk) ─────────────────────────────────────────────────────

export const spatialKatJobHistory = async (
  reportId: string
): Promise<client.CatJobHistoryResponse> => {
  return client.getCatJobHistory({
    reportId,
    type: "spatialkat",
  });
};

export const downloadAllSpatialKatArtifacts = async (
  reportId: string,
  batchId: string
): Promise<{ data: Blob; filename?: string }> => {
  return fetchBinary({
    method: "get",
    path: `/cat/download_all/${reportId}/${batchId}?type=spatialkat`,
    contentType: "application/zip",
  });
};

export const calculatePremium = async (reportId: string, batchId?: string) => {
  return client.calculatePremium({
    requestBody: { report_id: reportId, batch_id: batchId },
  });
};

export const getFinancialModelAudit = async (
  reportId: string,
  source: FinancialModelAuditSource,
  batchId?: string | null
) =>
  client.getFinancialModelAudit({
    requestBody: { report_id: reportId, source, batch_id: batchId ?? null },
  });

export const getIntegrationData = async (
  reportId: string,
  types: client.IntegrationDataRequest["types"],
  addressIds: string[]
): Promise<client.IntegrationDataResponse> => {
  return client.getIntegrationData({
    requestBody: { report_id: reportId, types, address_ids: addressIds },
  });
};

export const getDeductibleSummary = async (
  reportId: string,
  addressIds: string[]
): Promise<client.DeductibleSummaryResponse> => {
  return client.getDeductibleSummary({
    requestBody: { report_id: reportId, address_ids: addressIds },
  });
};

export const getLimitSummary = async (
  reportId: string,
  addressIds: string[]
): Promise<client.LimitSummaryResponse> => {
  return client.getLimitSummary({
    requestBody: { report_id: reportId, address_ids: addressIds },
  });
};

export const getRerunOptions = async (
  reportId: string
): Promise<client.RerunOptionsResponse> => {
  return client.asgardRerunOptions({ reportId });
};

export const getLayerGateExposure = async (
  reportId: string,
  layerId: string
): Promise<client.GateExposureResponse> => {
  return client.getLayerGateExposure({
    requestBody: { report_id: reportId, layer_id: layerId },
  });
};

export const getGateTrackingBase = async (
  reportId: string,
  layerId: string
): Promise<client.GateTrackingBaseResponse> => {
  return client.getGateTrackingBase({
    requestBody: { report_id: reportId, layer_id: layerId },
  });
};

export {
  getPortfolioGateAccumulation,
  getAalPremiumGatePortfolio,
} from "../client";

export const getAalPremiumGateTracking = async (
  reportId: string,
  layerId: string
): Promise<client.AalPremiumGateResponse> => {
  return client.getAalPremiumGateTracking({
    requestBody: { report_id: reportId, layer_id: layerId },
  });
};

export type WindTier = NonNullable<client.LocationClassification["wind_tier"]>;
export type WindGate = NonNullable<client.LocationClassification["wind_gate"]>;
export type EqGate = NonNullable<client.LocationClassification["eq_gate"]>;
export type LocationKey = client.LocationKey;
export type LocationClassificationKey = string & {
  readonly __brand: "LocationClassificationKey";
};

// Per-location catastrophe gate classifications, keyed off the same surface
// (state, county) strings the caller sent. Each field is null when the
// location doesn't fall into that peril's gate.
export type LocationClassification = {
  windTier: WindTier | null;
  windGate: WindGate | null;
  eqGate: EqGate | null;
};

export const getLocationClassifications = async (
  locations: LocationKey[]
): Promise<client.LocationClassificationsResponse> => {
  return client.getLocationClassifications({
    requestBody: { locations },
  });
};

export const getGoogleGroupStats = async (email: string) => {
  return client.asgardGoogleGroupStats({
    requestBody: { google_group_email: email },
  });
};

export const getClearanceEmailRecipients = async (
  reportId: string
): Promise<{ recipients: string[]; locked_emails: string[] }> => {
  return client.asgardClearanceEmailRecipients({
    requestBody: { report_id: reportId },
  });
};

export const listReportActionLogs = async (): Promise<
  client.ReportActionSummaryWithName[]
> => {
  return client
    .asgardListReportActionLogs({
      requestBody: {},
    })
    .then((response) => response.summaries);
};

export const getReportActionLog = async (
  id: string
): Promise<client.GetReportActionLogResponse> => {
  const isEmail = id.startsWith("email:");
  const requestBody = isEmail
    ? { email_message_id: id.slice("email:".length) }
    : { report_id: id };
  return client.asgardGetReportActionLog({
    requestBody,
  });
};

// ── BOR (Broker of Record) ──

export const createBorLetter = async (
  request: client.CreateBorLetterRequest
): Promise<client.BorLetterResponse> => {
  return client.createBorLetterEndpoint({
    requestBody: request,
  });
};

export const getBorLetter = async (
  borId: string
): Promise<client.BorLetterResponse> => {
  return client.getBorLetterEndpoint({
    requestBody: { bor_id: borId },
  });
};

export const getBorLettersByReport = async (
  reportId: string
): Promise<client.BorLetterResponse[]> => {
  return client
    .getBorLettersByReportEndpoint({
      requestBody: { report_id: reportId },
    })
    .then((r) => r.bor_letters);
};

export const listBorLetters = async (
  activeOnly: boolean = false
): Promise<client.BorLetterResponse[]> => {
  return client
    .listBorLettersEndpoint({
      requestBody: { active_only: activeOnly },
    })
    .then((r) => r.bor_letters);
};

export const markBorStep = async (
  borId: string,
  step: client.MarkBorStepRequest["step"]
): Promise<client.BorLetterResponse> => {
  return client.markBorStepEndpoint({
    requestBody: { bor_id: borId, step },
  });
};

export const borEmailPreview = async (
  borId: string,
  step: client.BorEmailRequest["step"]
): Promise<client.RenderedBorEmail> => {
  return client.borEmailPreviewEndpoint({
    requestBody: { bor_id: borId, step },
  });
};

export const sendBorEmail = async (
  borId: string,
  step: client.BorEmailRequest["step"]
): Promise<client.BorLetterResponse> => {
  return client.borSendEmailEndpoint({
    requestBody: { bor_id: borId, step },
  });
};

// --- Policy applications (see docs/policy-management-design.md) ---

export const listPolicyApplications = async (
  reportId: string
): Promise<client.PolicyApplicationView[]> => {
  const res = await client.asgardListPolicyApplications({
    requestBody: { report_id: reportId },
  });
  return res.applications;
};

export const createPolicyApplications = async (
  reportId: string
): Promise<client.PolicyApplicationView[]> => {
  const res = await client.asgardCreatePolicyApplications({
    requestBody: { report_id: reportId },
  });
  return res.applications;
};

export const getPolicyApplicationFields = async (
  applicationId: string
): Promise<client.PolicyApplicationFieldsResponse> => {
  return client.asgardPolicyApplicationFields({
    requestBody: { application_id: applicationId },
  });
};

export const updatePolicyOverlay = async (
  applicationId: string,
  overlay: client.PolicyFieldOverlay,
  notes: string | null
): Promise<client.PolicyApplicationFieldsResponse> => {
  return client.asgardUpdatePolicyOverlay({
    requestBody: { application_id: applicationId, overlay, notes },
  });
};

export const updatePolicyApplication = async (
  applicationId: string,
  status: client.PolicyApplicationStatus
): Promise<client.PolicyApplicationView> => {
  return client.asgardUpdatePolicyApplication({
    requestBody: { application_id: applicationId, status },
  });
};

export const getPolicyApplicationForms = async (
  applicationId: string
): Promise<client.PolicyApplicationFormsResponse> => {
  return client.asgardPolicyApplicationForms({
    requestBody: { application_id: applicationId },
  });
};

export const updateApplicationForm = async (
  applicationId: string,
  formId: string,
  enabled: boolean
): Promise<client.PolicyApplicationFormsResponse> => {
  return client.asgardUpdateApplicationForm({
    requestBody: {
      application_id: applicationId,
      form_id: formId,
      enabled,
    },
  });
};

// The application's effective quote template docx plus what the editor needs
// before its first preview render; `meta` is undefined only if the header is
// missing or malformed.
export const getApplicationQuoteTemplate = async (
  applicationId: string
): Promise<{ data: Blob; meta: QuoteTemplateMeta | undefined }> => {
  const { data, meta } = await fetchBinary({
    method: "post",
    path: "/asgard/policy/application_quote_template",
    body: { application_id: applicationId },
  });
  return { data, meta: parseQuoteTemplateMeta(meta) };
};

export const saveApplicationQuoteTemplate = async (
  applicationId: string,
  template: Blob
): Promise<void> => {
  await client.asgardSaveApplicationQuoteTemplate({
    formData: {
      application_id: applicationId,
      template: template,
    },
  });
};

export const resetApplicationQuoteTemplate = async (
  applicationId: string
): Promise<void> => {
  await client.asgardResetApplicationQuoteTemplate({
    requestBody: { application_id: applicationId },
  });
};

// The [[ UNKNOWN: … ]] marker names a quote render would leave in the letter,
// given the editor's current export. Renders nothing user-visible and
// persists nothing — used to confirm before attaching an unfinished letter.
export const getApplicationQuoteUnknowns = async (
  applicationId: string,
  template: Blob
): Promise<string[]> => {
  const response = await client.asgardApplicationQuoteUnknowns({
    formData: { application_id: applicationId, template },
  });
  return response.unknowns;
};

// One name a quote template can reference and what it renders to right now.
// Mirrors QuoteTemplateVariable in the backend's quote_builder.py.
export interface QuoteTemplateVariable {
  name: string;
  // Rendered text of a scalar; null for a list variable.
  value: string | null;
  // Number of items in a list variable; null for a scalar.
  item_count: number | null;
  // Display text of a list's first item; null for a scalar or an empty list.
  sample: string | null;
  // First row's fields when the list holds rows (for `c.name` completions).
  fields: Record<string, string> | null;
  unknown: boolean;
}

// Mirrors QuoteRenderMeta in the backend's policy_application_endpoints.py.
export interface QuoteRenderMeta {
  // [[ UNKNOWN: … ]] marker names left in the rendered letter.
  unknowns: string[];
  variables: QuoteTemplateVariable[];
}

const isQuoteTemplateVariable = (
  value: unknown
): value is QuoteTemplateVariable =>
  isRecord(value) &&
  typeof value.name === "string" &&
  (typeof value.value === "string" || value.value === null) &&
  (typeof value.item_count === "number" || value.item_count === null) &&
  (typeof value.sample === "string" || value.sample === null) &&
  (isRecord(value.fields) || value.fields === null) &&
  typeof value.unknown === "boolean";

const isQuoteTemplateVariableArray = (
  value: unknown
): value is QuoteTemplateVariable[] =>
  Array.isArray(value) && value.every(isQuoteTemplateVariable);

const parseQuoteRenderMeta = (meta: unknown): QuoteRenderMeta | undefined =>
  isRecord(meta) &&
  isStringArray(meta.unknowns) &&
  isQuoteTemplateVariableArray(meta.variables)
    ? { unknowns: meta.unknowns, variables: meta.variables }
    : undefined;

// Mirrors QuoteTemplateMeta in the backend's policy_application_endpoints.py.
export interface QuoteTemplateMeta {
  // Whether the served template is the application's saved customization
  // rather than the repo's base template.
  customized: boolean;
  // [[ UNKNOWN: … ]] marker names the template renders with right now; null
  // when the stored template no longer renders.
  unknowns: string[] | null;
  variables: QuoteTemplateVariable[];
}

const parseQuoteTemplateMeta = (
  meta: unknown
): QuoteTemplateMeta | undefined =>
  isRecord(meta) &&
  typeof meta.customized === "boolean" &&
  (isStringArray(meta.unknowns) || meta.unknowns === null) &&
  isQuoteTemplateVariableArray(meta.variables)
    ? {
        customized: meta.customized,
        unknowns: meta.unknowns,
        variables: meta.variables,
      }
    : undefined;

// Render the application's quote letter. Returns a pdf (or docx if the backend
// has no LibreOffice available — check the returned filename) plus the
// render's metadata (unknown markers, available variables); `meta` is
// undefined only if the header is missing or malformed. Passing the editor's
// current export as `template` renders it; `persistTemplate` also stores it
// as the saved layout (false for previews); `saveToReport` stores the
// rendered document as a report attachment.
export const generateApplicationQuote = async (
  applicationId: string,
  outputFormat: "pdf" | "docx",
  options: {
    template?: Blob;
    saveToReport: boolean;
    persistTemplate: boolean;
    fullPackage: boolean;
  }
): Promise<{
  data: Blob;
  filename?: string;
  meta: QuoteRenderMeta | undefined;
}> => {
  const form = new FormData();
  form.append("application_id", applicationId);
  form.append("output_format", outputFormat);
  form.append("save_to_report", String(options.saveToReport));
  form.append("persist_template", String(options.persistTemplate));
  form.append("full_package", String(options.fullPackage));
  if (options.template) {
    form.append("template", options.template, "template.docx");
  }
  const { data, filename, meta } = await fetchBinary({
    method: "post",
    path: "/asgard/policy/generate_application_quote",
    body: form,
  });
  return { data, filename, meta: parseQuoteRenderMeta(meta) };
};

export const listEndorsements = async (
  reportId: string
): Promise<client.ListEndorsementsResponse> => {
  return client.asgardListEndorsements({
    requestBody: { report_id: reportId },
  });
};

export const createEndorsement = async (input: {
  reportId: string;
  policyApplicationId: string;
  effectiveDate: string;
  description: string;
  premiumBasis: client.EndorsementPremiumBasis;
  tivTarget: number | null;
  customTermPremiumDelta: number | null;
  files: File[];
}): Promise<client.EndorsementView> => {
  return client.asgardCreateEndorsement({
    formData: {
      report_id: input.reportId,
      policy_application_id: input.policyApplicationId,
      effective_date: input.effectiveDate,
      description: input.description,
      premium_basis: input.premiumBasis,
      tiv_target: input.tivTarget,
      custom_term_premium_delta: input.customTermPremiumDelta,
      files: input.files.length > 0 ? input.files : null,
    },
  });
};

export const uploadEndorsementDocuments = async (
  endorsementId: string,
  files: File[]
): Promise<client.EndorsementView> => {
  return client.asgardUploadEndorsementDocuments({
    formData: { endorsement_id: endorsementId, files },
  });
};

export const suggestEndorsementPremium = async (
  endorsementId: string
): Promise<client.EndorsementPremiumChange> => {
  return client.asgardSuggestEndorsementPremium({
    requestBody: { endorsement_id: endorsementId },
  });
};

export const updateEndorsementPremium = async (
  endorsementId: string,
  premiumChange: client.EndorsementPremiumChange
): Promise<client.EndorsementView> => {
  return client.asgardUpdateEndorsementPremium({
    requestBody: {
      endorsement_id: endorsementId,
      premium_change: premiumChange,
    },
  });
};

export const updateEndorsementStatus = async (
  endorsementId: string,
  status: client.EndorsementStatus
): Promise<client.EndorsementView> => {
  return client.asgardUpdateEndorsementStatus({
    requestBody: { endorsement_id: endorsementId, status },
  });
};

export const deleteEndorsement = async (
  endorsementId: string
): Promise<client.DeleteEndorsementResponse> => {
  return client.asgardDeleteEndorsement({
    requestBody: { endorsement_id: endorsementId },
  });
};

export const estimateEndorsementPremium = async (input: {
  reportId: string;
  policyApplicationId: string;
  effectiveDate: string;
  tivTarget: number;
}): Promise<client.EndorsementPremiumChange> => {
  return client.asgardEstimateEndorsementPremium({
    requestBody: {
      report_id: input.reportId,
      policy_application_id: input.policyApplicationId,
      effective_date: input.effectiveDate,
      tiv_target: input.tivTarget,
    },
  });
};

export const getEndorsementSovDiff = async (
  endorsementId: string
): Promise<client.EndorsementSovDiffResponse> => {
  return client.asgardEndorsementSovDiff({
    requestBody: { endorsement_id: endorsementId },
  });
};

export const regenerateEndorsement = async (
  endorsementId: string
): Promise<client.EndorsementView> => {
  return client.asgardRegenerateEndorsement({
    requestBody: { endorsement_id: endorsementId },
  });
};
